import * as THREE from "three";

import { FixedStepLoop } from "../core/fixed-step-loop";
import { InputManager } from "../core/input";
import type { GameScene } from "../core/scene-lifecycle";
import type { WeaponId } from "../content/schemas";
import { createOnlineGameService } from "../online/create-online-service";
import type { OnlineGameService } from "../online/contracts";
import { ZONEVELOCITY_GAME_ID } from "../online/contracts";
import { LocalProfileStore } from "../online/local-profile";
import { AudioBus } from "../presentation/audio-bus";
import { CarrierBay } from "../presentation/carrier-bay";
import { CombatJuice, type CombatJuiceSample } from "../presentation/combat-juice";
import { ComboMeter } from "../presentation/combo-meter";
import { FpsOverlay } from "../presentation/fps-overlay";
import { HudController } from "../presentation/hud";
import { HapticsBus } from "../presentation/haptics";
import { LeaderboardPanel } from "../presentation/leaderboard";
import { PauseStatusPanel } from "../presentation/pause-status";
import { RunSummaryModal } from "../presentation/run-summary";
import { ScoreFeed } from "../presentation/score-feed";
import { ScorePopupLayer } from "../presentation/score-popups";
import { SettingsOverlay } from "../presentation/settings-overlay";
import { SkillPickPanel, skillPickAllowed } from "../presentation/skill-pick";
import { TelemetrySink } from "../presentation/telemetry";
import { TouchControls } from "../presentation/touch-controls";
import { WorldLabelLayer } from "../presentation/world-labels";
import type { BankedProfile } from "../simulation/persistence";
import type { PlayerCommand } from "../simulation/commands";
import { ExpeditionSimulation } from "../simulation/simulation";
import type { WorldSnapshot } from "../simulation/snapshot";
import { createIndustrialEnvMap } from "./environment";
import { PostFxPipeline } from "./post-fx";
import { AdaptiveQualityController } from "./adaptive-quality";
import { resolveRenderQuality, type RenderQualityId } from "./quality";
import { WorldView } from "./world-view";
import { installVisualQaStamp, visualSceneFromLocation } from "../visual-qa";

export type GameSceneHostElements = {
  canvas: HTMLCanvasElement;
  shell: HTMLElement;
  combatHud: HTMLElement;
  status: HTMLElement;
  vitals: HTMLElement;
  inventory: HTMLElement;
  band: HTMLElement;
  help: HTMLElement;
  message: HTMLElement;
  levelBadge: HTMLElement;
  xpBar: HTMLElement;
  xpLabel: HTMLElement;
  score: HTMLElement;
  comboMeter: HTMLElement;
  comboTag: HTMLElement;
  scoreFeed: HTMLElement;
  streak: HTMLElement;
  jammer: HTMLElement;
  incursion: HTMLElement;
  hitVignette: HTMLElement;
  carrierNav: HTMLElement;
  affixChips: HTMLElement;
  loadoutRail: HTMLElement;
  runChips: HTMLElement;
  softLockReticle: HTMLElement;
  outcomeBeat: HTMLElement;
};

/**
 * Thin host: input → command buffer → pure sim → snapshot → render/HUD/audio.
 */
export class ZoneVelocityScene implements GameScene {
  private readonly renderer: THREE.WebGLRenderer;
  private readonly input = new InputManager();
  private readonly view = new WorldView();
  private readonly postFx: PostFxPipeline;
  private envMap: ReturnType<typeof createIndustrialEnvMap> | null = null;
  private readonly audio = new AudioBus();
  private readonly telemetry = new TelemetrySink();
  private readonly profileStore = new LocalProfileStore();
  private readonly online: OnlineGameService;
  private readonly hud: HudController;
  private readonly touch: TouchControls;
  private readonly bay: CarrierBay;
  private readonly settings: SettingsOverlay;
  private readonly pausePanel: PauseStatusPanel;
  private readonly skillPick: SkillPickPanel;
  private readonly comboMeter: ComboMeter;
  private readonly scoreFeed: ScoreFeed;
  private readonly scorePopups: ScorePopupLayer;
  private readonly worldLabels: WorldLabelLayer;
  private readonly runSummary: RunSummaryModal;
  private readonly haptics = new HapticsBus();
  private readonly leaderboard: LeaderboardPanel;
  private readonly juice = new CombatJuice();
  private readonly fpsOverlay: FpsOverlay;
  private readonly visualScene = visualSceneFromLocation();
  private adaptive: AdaptiveQualityController | null = null;
  private lastAdaptiveSetting: RenderQualityId = "auto";
  private readonly loop: FixedStepLoop;
  private leaderboardEntries: ReturnType<typeof LeaderboardPanel.loadLocal> = [];
  private lastSubmittedScore = -1;
  private sim: ExpeditionSimulation;
  private snapshot: WorldSnapshot;
  private lastCommand: PlayerCommand | null = null;
  private juiceSample: CombatJuiceSample | null = null;
  private seenScoreFeedIds = new Set<number>();
  private lastJuiceSampleMs = 0;
  private entered = false;
  private skillPickLockedUntil = 0;
  private debriefHoldUntilMs = 0;
  private lastOutcomePhase: WorldSnapshot["phase"] | null = null;
  private lastCombatAffixKey = "";
  private visualPerfStartedMs = 0;
  private visualPerfLastFrameMs = 0;
  private readonly visualFrameTimes: number[] = [];

  public constructor(private readonly elements: GameSceneHostElements) {
    const banked = this.profileStore.loadBanked();
    if (this.visualScene) {
      banked.runsCompleted = Math.max(2, banked.runsCompleted);
      banked.settings.renderQuality = this.visualScene.quality;
      banked.settings.showTouchControls = this.visualScene.id === "touch-combat";
      banked.settings.reducedMotion = true;
    }
    const initialQuality = resolveRenderQuality(
      banked.settings.renderQuality,
      banked.settings.reducedMotion,
    );
    // Composer AA (SMAA/FXAA) replaces MSAA; MSAA on the default buffer is wasted with post.
    this.renderer = new THREE.WebGLRenderer({
      canvas: elements.canvas,
      antialias: !initialQuality.postFx,
      powerPreference: "high-performance",
      preserveDrawingBuffer: false,
    });
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = 1.05;
    this.renderer.setClearColor(0x14110e);
    this.postFx = new PostFxPipeline(this.renderer, this.view.scene, this.view.camera);
    this.fpsOverlay = new FpsOverlay(elements.shell);

    const identity = this.profileStore.getOrCreateIdentity();
    this.online = createOnlineGameService(identity, this.profileStore);

    this.applyRenderQuality(banked.settings.renderQuality, banked.settings.reducedMotion);
    this.sim = new ExpeditionSimulation({
      banked,
      seed: this.visualScene?.seed,
      onboarding: this.visualScene ? false : undefined,
    });
    const run = this.profileStore.loadRun();
    if (
      !this.visualScene &&
      run &&
      run.phase !== "dead" &&
      run.phase !== "extracted" &&
      run.phase !== "carrier"
    ) {
      this.sim.restoreRun(run);
    }
    this.snapshot = this.sim.step(this.input.sampleCommand(0));
    if (this.visualScene) this.snapshot = this.sim.applyVisualFixture(this.visualScene.id);

    this.hud = new HudController({
      combatHud: elements.combatHud,
      status: elements.status,
      vitals: elements.vitals,
      inventory: elements.inventory,
      band: elements.band,
      help: elements.help,
      message: elements.message,
      levelBadge: elements.levelBadge,
      xpBar: elements.xpBar,
      xpLabel: elements.xpLabel,
      score: elements.score,
      streak: elements.streak,
      jammer: elements.jammer,
      incursion: elements.incursion,
      hitVignette: elements.hitVignette,
      carrierNav: elements.carrierNav,
      affixChips: elements.affixChips,
      loadoutRail: elements.loadoutRail,
      runChips: elements.runChips,
      softLockReticle: elements.softLockReticle,
      outcomeBeat: elements.outcomeBeat,
    });

    this.touch = new TouchControls(elements.shell, this.input);
    this.settings = new SettingsOverlay(document.body, {
      onSettings: (settings) => {
        this.applySettings(settings);
        this.refreshTouch();
      },
      onShowFps: (show) => this.fpsOverlay.setEnabled(show),
    });
    this.bay = new CarrierBay(elements.shell, {
      onCraft: (recipeId) => {
        const ok = this.sim.craft(recipeId);
        this.snapshot = this.sim.captureSnapshot();
        this.bay.setCraftFeedback(this.snapshot.message);
        this.persistBanked();
        this.bay.sync(this.sim.getBanked(), this.snapshot);
        if (ok) this.audio.handle(this.snapshot.audioEvents);
      },
      onFrame: (frameId) => {
        this.sim.selectFrame(frameId);
        this.snapshot = this.sim.captureSnapshot();
        this.persistBanked();
        this.bay.sync(this.sim.getBanked(), this.snapshot);
      },
      onTrail: (trailId) => {
        this.sim.selectTrail(trailId);
        this.snapshot = this.sim.captureSnapshot();
        this.persistBanked();
        this.bay.sync(this.sim.getBanked(), this.snapshot);
      },
      onToggleWeapon: (weaponId: WeaponId) => {
        this.sim.toggleWeapon(weaponId);
        this.snapshot = this.sim.captureSnapshot();
        this.persistBanked();
        this.bay.sync(this.sim.getBanked(), this.snapshot);
      },
      onCalibrate: (weaponId, axis) => {
        const ok = this.sim.calibrateWeapon(weaponId, axis);
        this.snapshot = this.sim.captureSnapshot();
        this.bay.setCraftFeedback(this.snapshot.message);
        this.persistBanked();
        this.bay.sync(this.sim.getBanked(), this.snapshot);
        if (ok) this.audio.handle(this.snapshot.audioEvents);
      },
      onContract: (contractId) => {
        const ok = this.sim.queueSortieContract(contractId);
        this.snapshot = this.sim.captureSnapshot();
        this.bay.setCraftFeedback(this.snapshot.message);
        this.persistBanked();
        this.bay.sync(this.sim.getBanked(), this.snapshot);
        if (ok) this.audio.handle(this.snapshot.audioEvents);
      },
      onLaunch: () => {
        this.sim.requestLaunch();
        this.persistBanked();
        this.profileStore.saveRun(this.sim.serializeRun());
      },
      onOpenSettings: () => this.settings.open(),
    });
    this.pausePanel = new PauseStatusPanel(elements.shell, {
      onOpenSettings: () => this.settings.open(),
      onResume: () => this.input.setTouchButton("pause", true),
    });
    this.skillPick = new SkillPickPanel(elements.shell, {
      onPickIndex: (index) => this.resolveSkillPick(index),
      onReroll: () => {
        if (this.sim.rerollSkillPick()) {
          this.snapshot = this.sim.captureSnapshot();
          this.skillPick.sync(this.snapshot, this.sim.getBanked().pilotMeta);
        }
      },
      onBanish: (index) => {
        if (this.sim.banishSkillPick(index)) {
          this.snapshot = this.sim.captureSnapshot();
          this.skillPick.sync(this.snapshot, this.sim.getBanked().pilotMeta);
        }
      },
    });
    this.comboMeter = new ComboMeter(elements.comboMeter, elements.comboTag);
    this.scoreFeed = new ScoreFeed(elements.scoreFeed);
    this.scorePopups = new ScorePopupLayer(elements.shell);
    this.worldLabels = new WorldLabelLayer(elements.shell);
    this.runSummary = new RunSummaryModal(elements.shell, {
      onRelaunch: () => {
        this.sim.relaunch();
        this.skillPick.forceHide();
        this.snapshot = this.sim.step(this.input.sampleCommand(this.snapshot.tick));
        this.persistBanked();
      },
    });
    this.leaderboard = new LeaderboardPanel(elements.shell);
    void this.online.getLeaderboard().then((entries) => {
      this.leaderboardEntries = entries;
    });
    this.bay.sync(this.sim.getBanked(), this.snapshot);
    this.pausePanel.sync(this.sim.getBanked(), this.snapshot);
    this.settings.sync(this.sim.getBanked());
    this.sim.ensureSkillPickOffers();
    this.snapshot = { ...this.snapshot, runProgress: this.sim.getRunProgress() };
    this.skillPick.sync(this.snapshot, this.sim.getBanked().pilotMeta);

    this.elements.help.dataset.gameId = ZONEVELOCITY_GAME_ID;
    void this.online.getIdentity().then((id) => {
      this.elements.help.dataset.player = id.id;
    });

    this.input.attachCanvas(elements.canvas);
    this.audio.bindGestureUnlock(elements.shell);
    this.syncAudioFromSettings();
    // Bind explicitly — FixedStepLoop invokes callbacks without a receiver.
    this.loop = new FixedStepLoop({
      update: (deltaSeconds) => this.update(deltaSeconds),
      render: (interpolation) => this.render(interpolation),
    });
    if (this.visualScene) installVisualQaStamp(elements.shell, this.visualScene);
  }

  public enter(): void {
    if (this.entered) return;
    this.entered = true;
    this.syncTouchPlayClass();
    const { width, height } = this.elements.shell.getBoundingClientRect();
    this.resize(width, height);
    this.refreshTouch();
    this.elements.status.hidden = true;
    if (this.visualScene) {
      this.view.freezePresentationAt(this.visualScene.tick / 60);
      this.view.sync(this.snapshot);
      this.hud.render(this.snapshot, this.sim.getBanked());
      this.comboMeter.sync(this.snapshot.combo);
      this.scoreFeed.sync(this.snapshot.scoreFeed);
      this.runSummary.sync(this.snapshot.runSummary, this.sim.getBanked().pilotMeta, {
        reducedMotion: true,
        banked: this.sim.getBanked(),
      });
    }
    this.loop.start();
  }

  public start(): void {
    this.enter();
  }

  public update(deltaSeconds: number): void {
    if (this.visualScene) return;
    this.juice.setReducedMotion(this.snapshot.settings.reducedMotion);

    // Micro hitstop: skip sim step while freeze frames remain.
    if (this.juice.tickHitstop(deltaSeconds)) {
      return;
    }

    const menuOpen = this.snapshot.runSummary !== null || this.settings.isOpen();
    const inBay =
      this.snapshot.phase === "dead" ||
      this.snapshot.phase === "extracted" ||
      this.snapshot.phase === "carrier";

    if (
      !menuOpen &&
      !inBay &&
      !this.snapshot.paused &&
      this.snapshot.tick >= this.skillPickLockedUntil
    ) {
      this.sim.ensureSkillPickOffers();
      this.snapshot = { ...this.snapshot, runProgress: this.sim.getRunProgress() };
    }

    const skillPickOpen = skillPickAllowed(this.snapshot) && !menuOpen;
    this.input.setInputGate({ skillPick: skillPickOpen, menu: menuOpen });
    this.refreshTouch(skillPickOpen || menuOpen);
    this.elements.canvas.style.pointerEvents = skillPickOpen || menuOpen ? "none" : "auto";

    if (this.input.consumePress("mute")) {
      const next = !this.sim.getBanked().settings.muted;
      this.sim.setSetting("muted", next);
      this.persistBanked();
      this.syncAudioFromSettings();
    }
    if (this.input.consumePress("musicToggle")) {
      const next = !this.sim.getBanked().settings.musicEnabled;
      this.sim.setSetting("musicEnabled", next);
      this.persistBanked();
      this.syncAudioFromSettings();
    }

    const command = this.input.sampleCommand(this.snapshot.tick + 1);
    this.lastCommand = command;
    this.snapshot = this.sim.step(command);
    this.view.sync(this.snapshot);

    const phase = this.snapshot.phase;
    if ((phase === "dead" || phase === "extracted") && phase !== this.lastOutcomePhase) {
      const holdMs = phase === "dead" ? 1100 : 900;
      this.debriefHoldUntilMs = performance.now() + holdMs;
      this.lastOutcomePhase = phase;
      if (phase === "dead" && !this.snapshot.settings.reducedMotion) {
        this.juice.ingest([{ type: "death" }], this.snapshot.player, this.snapshot.combo);
      }
    }
    if (phase !== "dead" && phase !== "extracted") {
      this.lastOutcomePhase = null;
      this.debriefHoldUntilMs = 0;
    }
    const debriefHold = performance.now() < this.debriefHoldUntilMs;

    const banked = this.sim.getBanked();
    this.hud.render(this.snapshot, banked, { debriefHold });
    if (this.snapshot.coachPulse === "arc_torp") {
      this.touch.pulseWeaponButtons();
    }
    this.comboMeter.sync(this.snapshot.combo);
    this.scoreFeed.sync(this.snapshot.scoreFeed);
    this.runSummary.sync(debriefHold ? null : this.snapshot.runSummary, banked.pilotMeta, {
      reducedMotion: this.snapshot.settings.reducedMotion,
      banked,
    });
    const affixKey = this.snapshot.runInventory.affixes.join(",");
    if (affixKey !== this.lastCombatAffixKey) {
      this.audio.setCombatContext({
        affixes: this.snapshot.runInventory.affixes,
      });
      this.lastCombatAffixKey = affixKey;
    }
    this.audio.handle(this.snapshot.audioEvents, {
      x: this.snapshot.player.x,
      y: this.snapshot.player.y,
      facing: this.snapshot.player.facing,
    });

    const freshFeed: typeof this.snapshot.scoreFeed = [];
    for (const entry of this.snapshot.scoreFeed) {
      if (!this.seenScoreFeedIds.has(entry.id)) freshFeed.push(entry);
      this.seenScoreFeedIds.add(entry.id);
    }
    if (this.seenScoreFeedIds.size > 64) {
      this.seenScoreFeedIds.clear();
      for (const entry of this.snapshot.scoreFeed) this.seenScoreFeedIds.add(entry.id);
    }

    this.juice.ingest(
      this.snapshot.audioEvents,
      this.snapshot.player,
      this.snapshot.combo,
      freshFeed,
    );
    this.view.ingestCombatEvents(this.snapshot.audioEvents, {
      x: this.snapshot.player.x,
      y: this.snapshot.player.y,
      altitude: this.snapshot.player.altitude,
      facing: this.snapshot.player.facing,
    });

    for (const event of this.snapshot.audioEvents) {
      if (event.type === "enemy_death" && this.snapshot.combo.chainCount >= 2) {
        this.audio.playKillConfirm(this.snapshot.combo.chainCount);
      }
    }
    if (freshFeed.some((e) => e.kind === "chain" && e.points > 0)) {
      this.audio.playScoreTick();
    }

    this.telemetry.ingest(this.snapshot.telemetryEvents);
    this.bay.setScores(this.leaderboardEntries);
    this.bay.sync(banked, this.snapshot);
    this.pausePanel.sync(banked, this.snapshot);
    this.settings.sync(banked);

    const debriefOpen = this.snapshot.runSummary !== null;
    const docked =
      this.snapshot.phase === "dead" ||
      this.snapshot.phase === "extracted" ||
      this.snapshot.phase === "carrier";
    if (debriefOpen || docked) {
      this.skillPick.forceHide();
    } else {
      this.skillPick.sync(this.snapshot, banked.pilotMeta);
    }
    this.leaderboard.sync(this.leaderboardEntries, false);

    for (const event of this.snapshot.audioEvents) {
      if (event.type === "hull_hit") this.haptics.hullHit();
      if (event.type === "extract_complete") this.haptics.extractComplete();
      if (event.type === "assist_lock") this.haptics.softLock();
    }
    if (this.snapshot.scoreFeed.some((e) => e.kind === "double_kill" || e.kind === "triple_kill")) {
      this.haptics.multiKill();
    }
    const readyTicks = this.touch.pollWeaponReady(this.snapshot);
    if (readyTicks.secondary || readyTicks.ordnance) this.haptics.weaponReady();

    const lastRun = this.sim.getBanked().lastRun;
    if (
      lastRun &&
      (this.snapshot.phase === "extracted" || this.snapshot.phase === "dead") &&
      lastRun.score !== this.lastSubmittedScore
    ) {
      this.lastSubmittedScore = lastRun.score;
      void this.online
        .submitScore({
          gameId: ZONEVELOCITY_GAME_ID,
          score: lastRun.score,
          occurredAt: new Date().toISOString(),
          metadata: {
            level: lastRun.level,
            bestStreak: lastRun.bestStreak,
            outcome: lastRun.outcome,
            simTime: this.snapshot.simTime,
            totalKills: this.snapshot.runSummary?.totalKills ?? 0,
          },
        })
        .then(() =>
          this.online.getLeaderboard().then((e) => {
            this.leaderboardEntries = e;
          }),
        );
    }

    if (this.snapshot.tick % 60 === 0) {
      this.profileStore.saveRun(this.sim.serializeRun());
      this.persistBanked();
    }
    if (
      this.snapshot.phase === "extracted" ||
      this.snapshot.phase === "dead" ||
      this.snapshot.phase === "carrier"
    ) {
      this.sim.refreshDailyOps();
      this.persistBanked();
      if (this.snapshot.phase === "extracted" || this.snapshot.phase === "dead") {
        this.profileStore.clearRun();
      }
    }
  }

  public render(interpolation = 1): void {
    const boosting = !!this.lastCommand?.buttons.boost && this.snapshot.player.boostFuel > 0.05;
    const now = performance.now();
    const dt =
      this.lastJuiceSampleMs > 0 ? Math.min(0.05, (now - this.lastJuiceSampleMs) / 1000) : 1 / 60;
    this.lastJuiceSampleMs = now;
    this.juiceSample = this.juice.sample(dt, boosting);
    this.view.renderFrame(interpolation, this.snapshot.settings.reducedMotion, this.juiceSample);
    this.postFx.render();
    this.sampleVisualPerformance(now);
    this.scorePopups.render(this.snapshot, this.view.camera, this.elements.canvas);
    this.worldLabels.render(this.snapshot, this.view.camera, this.elements.canvas);
    this.fpsOverlay.tick();
    if (this.adaptive && this.snapshot.settings.renderQuality === "auto") {
      const next = this.adaptive.sample(dt);
      if (next) this.applyResolvedQuality(next, this.snapshot.settings.reducedMotion);
    }
  }

  private sampleVisualPerformance(now: number): void {
    if (!this.visualScene) return;
    if (this.visualPerfStartedMs === 0) this.visualPerfStartedMs = now;
    const elapsedMs = now - this.visualPerfStartedMs;
    if (this.visualPerfLastFrameMs > 0 && elapsedMs >= 3000 && elapsedMs <= 13000) {
      this.visualFrameTimes.push(now - this.visualPerfLastFrameMs);
    }
    this.visualPerfLastFrameMs = now;
    const sorted = [...this.visualFrameTimes].sort((a, b) => a - b);
    const p95Index = Math.max(0, Math.ceil(sorted.length * 0.95) - 1);
    const terrain = this.view.terrainStats();
    window.__ZONEVELOCITY_VISUAL_PERF__ = {
      complete: elapsedMs >= 13000,
      elapsedMs,
      p95FrameMs: sorted[p95Index] ?? 0,
      maxFrameMs: sorted[sorted.length - 1] ?? 0,
      drawCalls: this.renderer.info.render.calls,
      terrainVertexCount: terrain.vertexCount,
      terrainRebuildMs: terrain.rebuildMs,
      quality: terrain.quality,
    };
  }

  public resize(width: number, height: number): void {
    if (width === 0 || height === 0) return;
    this.view.resize(width, height);
    this.renderer.setSize(width, height, false);
    this.postFx.setSize(width, height);
  }

  public dispose(): void {
    this.loop.stop();
    this.input.dispose();
    this.touch.dispose();
    this.bay.dispose();
    this.settings.dispose();
    this.pausePanel.dispose();
    this.skillPick.dispose();
    this.scorePopups.dispose();
    this.worldLabels.dispose();
    this.fpsOverlay.dispose();
    this.runSummary.dispose();
    this.leaderboard.dispose();
    this.postFx.dispose();
    this.envMap?.dispose();
    this.envMap = null;
    this.view.dispose();
    this.renderer.dispose();
    document.documentElement.classList.remove("touch-play");
    this.entered = false;
  }

  public getTelemetry(): TelemetrySink {
    return this.telemetry;
  }

  private persistBanked(): void {
    this.profileStore.saveBanked(this.sim.getBanked());
  }

  private applySettings(settings: BankedProfile["settings"]): void {
    this.sim.setSetting("focusFalloff", settings.focusFalloff);
    this.sim.setSetting("reducedMotion", settings.reducedMotion);
    this.sim.setSetting("showTouchControls", settings.showTouchControls);
    this.sim.setSetting("colorblindTelegraphs", settings.colorblindTelegraphs);
    this.sim.setSetting("musicEnabled", settings.musicEnabled);
    this.sim.setSetting("musicVolume", settings.musicVolume);
    this.sim.setSetting("sfxVolume", settings.sfxVolume);
    this.sim.setSetting("muted", settings.muted);
    this.sim.setSetting("renderQuality", settings.renderQuality);
    this.persistBanked();
    this.syncAudioFromSettings();
    this.applyRenderQuality(settings.renderQuality, settings.reducedMotion);
    document.documentElement.classList.toggle(
      "colorblind-telegraphs",
      settings.colorblindTelegraphs,
    );
  }

  private syncAudioFromSettings(): void {
    const settings = this.sim.getBanked().settings;
    this.audio.setMuted(settings.muted);
    this.audio.setMusicVolume(settings.musicVolume);
    this.audio.setSfxVolume(settings.sfxVolume);
    this.audio.setMusicEnabled(settings.musicEnabled);
  }
  private applyRenderQuality(setting: RenderQualityId, reducedMotion: boolean): void {
    const quality = resolveRenderQuality(setting, reducedMotion);
    this.applyResolvedQuality(quality.id, reducedMotion);
    if (setting === "auto") {
      if (!this.adaptive || this.lastAdaptiveSetting !== "auto") {
        this.adaptive = new AdaptiveQualityController(quality.id, quality.id);
      } else {
        this.adaptive.reset(quality.id, quality.id);
      }
    } else {
      this.adaptive = null;
    }
    this.lastAdaptiveSetting = setting;
  }

  private applyResolvedQuality(
    tier: Exclude<RenderQualityId, "auto">,
    reducedMotion: boolean,
  ): void {
    const quality = resolveRenderQuality(tier, reducedMotion);
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, quality.maxPixelRatio));
    if (quality.envMap && !this.envMap) {
      this.envMap = createIndustrialEnvMap(this.renderer);
    } else if (!quality.envMap && this.envMap) {
      this.envMap.dispose();
      this.envMap = null;
    }
    this.view.applyQuality(quality, this.envMap?.texture ?? null);
    this.postFx.applyQuality(quality);
    this.postFx.setPixelRatio(Math.min(window.devicePixelRatio, quality.maxPixelRatio));
  }

  private resolveSkillPick(index: number): void {
    this.skillPick.forceHide();
    this.skillPickLockedUntil = this.snapshot.tick + 3;

    const progress = this.sim.getRunProgress();
    if (progress.skillPickOffers.length > 0) {
      const pickIndex = Math.max(0, Math.min(index, progress.skillPickOffers.length - 1));
      if (!this.sim.pickSkillByIndex(pickIndex)) {
        this.sim.skipSkillPick();
      }
    } else if (progress.unspentSkillPoints > 0) {
      this.sim.ensureSkillPickOffers();
      if (!this.sim.skipSkillPick()) {
        this.sim.forfeitSkillPick();
      }
    }

    this.snapshot = this.sim.captureSnapshot();

    if (skillPickAllowed(this.snapshot)) {
      this.skillPick.sync(this.snapshot, this.sim.getBanked().pilotMeta);
      return;
    }
    this.skillPick.forceHide();
  }

  private refreshTouch(modalOpen = false): void {
    if (modalOpen) {
      this.touch.setVisible(false);
      this.syncTouchPlayClass();
      return;
    }
    const force = this.sim.getBanked().settings.showTouchControls;
    const coarse = window.matchMedia("(pointer: coarse)").matches;
    const narrow = window.matchMedia("(max-width: 1024px)").matches;
    const touchUi =
      force || coarse || narrow || window.matchMedia("(hover: none) and (pointer: coarse)").matches;
    this.touch.setVisible(touchUi);
    this.touch.sync(this.snapshot);
    this.syncTouchPlayClass();
  }

  /** Lock page scroll/selection while touch UI is active. */
  private syncTouchPlayClass(): void {
    const coarse = window.matchMedia("(pointer: coarse)").matches;
    const narrow = window.matchMedia("(max-width: 1024px)").matches;
    const touchDevice = coarse || narrow || window.matchMedia("(hover: none)").matches;
    document.documentElement.classList.toggle("touch-play", touchDevice);
  }
}
